// auth.jsx
// Sign-in / sign-up modal. Magic-link first (friendlier for non-technical users),
// Google OAuth + Phone as alternatives. Phone is a stub — Step 7 wires SMS.
// Loaded after shared.jsx, before landing.jsx, in index.html.

const { useState: useStateA, useEffect: useEffectA, useRef: useRefA } = React;

function AuthModal({ open, mode = "sign-in", initialMode, onClose }) {
  // P338 — `t` is hoisted to the modal's top so the toast handlers
  // (sendMagicLink, onResend, continueWithGoogle, continueWithPhone)
  // can localize. Pre-fix the toast strings were hardcoded English
  // even though the form + sent view were already translated by
  // P323/P324, so a Russian/Uzbek user saw a fully translated modal
  // until something failed.
  const { t } = window.useT();
  const [view, setView] = useStateA(initialMode || mode); // "sign-in" | "sign-up" | "sent"
  const [email, setEmail] = useStateA("");
  const [name, setName] = useStateA("");
  const [company, setCompany] = useStateA(""); // honeypot — never visible to humans
  const [submitting, setSubmitting] = useStateA(false);
  const [resentAt, setResentAt] = useStateA(0);
  const [formLoadedAt, setFormLoadedAt] = useStateA(0);

  useEffectA(() => {
    if (open) {
      setView(initialMode || mode);
      setSubmitting(false);
      setFormLoadedAt(Date.now());
    }
  }, [open, mode, initialMode]);

  const isSignUp = view === "sign-up";
  // P535 — tightened class from `\S+` to `[^@\s]+` so the inline
  // validity gate matches the server (no client/server split where
  // `a@b@c.d` passes one but fails the other).
  const validEmail = /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email);
  const valid = validEmail && (!isSignUp || name.trim().length >= 2);

  const [magicLink, setMagicLink] = useStateA(null);
  const [emailSent, setEmailSent] = useStateA(false);
  const sendMagicLink = async (e) => {
    e?.preventDefault?.();
    if (!valid) return;
    setSubmitting(true);
    try {
      // P394 — X-Forge-Lang so the server's 400 messages (Invalid
       // email / Email too long, P393) come back in the user's
       // picked locale. The user might be anon at this point so
       // _apiHeaders() returns null; threading the header inline.
      const res = await fetch("/api/auth/request-link", {
        method: "POST",
        headers: { "content-type": "application/json", ...window.langHeader() },
        body: JSON.stringify({
          email,
          name: isSignUp ? name : undefined,
          company,                         // honeypot — must stay empty
          formLoadedAt: formLoadedAt || undefined,
        }),
      });
      // P252 — defensive JSON parse. Pre-fix `await res.json()` threw
      // raw "Unexpected token <" if the server returned HTML (edge
      // cache miss, gateway 502 page) or truncated mid-response. The
      // user got a useless "Sign-in failed: Unexpected token <" toast
      // instead of a status-aware message. The 200-handler at the same
      // file (line 123 re-issue / resend path) already used this
      // `.catch(() => ({}))` pattern; aligning the primary path so
      // every non-2xx surfaces the status code, and 200 with bad body
      // surfaces a clear "unexpected response" message.
      const j = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(j.error || `Status ${res.status}`);
      // sent=true → real email delivered, no shortcut. sent=false → dev path,
      // we render the "Open the magic link" card with the returned URL.
      setEmailSent(j.sent === true);
      setMagicLink(j.magicLink || null);
      setView("sent");
    } catch (err) {
      window.toast(`${t("auth.toastSignInFailed")}: ${err.message}`, { tone: "warn" });
    } finally {
      setSubmitting(false);
    }
  };

  // The verify endpoint sets the HttpOnly cookie server-side and redirects
  // to "/", so we navigate the whole window. Once /api/auth/me confirms,
  // App's boot bridges the cookie into localStorage auth state.
  const completeMagicLink = () => {
    if (magicLink) window.location.href = magicLink;
    else { window.signIn(email, { name: isSignUp ? name : undefined }); onClose?.(); }
  };

  // Google OAuth is not wired yet — the previous stub flipped the
  // client-side __authState to a hardcoded "you@gmail.com" with no
  // server-side cookie, so the very next API call 401'd and the
  // expiry handler reset the state. The button visibly "signed in
  // then signed out" with no explanation. Surface the same "coming
  // soon" treatment as Phone until real OAuth lands.
  const continueWithGoogle = () => {
    window.toast(t("auth.toastGoogleComingSoon"), { tone: "warn" });
  };

  const continueWithPhone = () => {
    window.toast(t("auth.toastPhoneComingSoon"));
  };

  const titleBy = {
    "sign-in": t("auth.signInTitle"),
    "sign-up": t("auth.signUpTitle"),
    "sent": t("auth.sentTitle"),
  };
  const kickerBy = {
    "sign-in": t("auth.signInKicker"),
    "sign-up": t("auth.signUpKicker"),
    "sent": t("auth.sentKicker"),
  };

  return (
    <ModalShell
      open={open}
      onClose={onClose}
      title={titleBy[view]}
      kicker={kickerBy[view]}
      width={440}
    >
      {view === "sent" ? (
        <SentView
          email={email}
          emailSent={emailSent}
          onResend={async () => {
            setResentAt(Date.now());
            try {
              // P394 — same X-Forge-Lang threading on the resend path.
              const r = await fetch("/api/auth/request-link", {
                method: "POST",
                headers: { "content-type": "application/json", ...window.langHeader() },
                body: JSON.stringify({ email, name: isSignUp ? name : undefined }),
              });
              // P213 — read the response. Pre-fix the resend toasted
              // success even on 429 (per-email + per-IP rate limit was
              // hit) or any other non-OK status, which was actively
              // misleading.
              if (r.status === 429) {
                window.toast(t("auth.toastRateLimited"), { tone: "warn" });
                return;
              }
              if (!r.ok) {
                const j = await r.json().catch(() => ({}));
                throw new Error(j.error || `status ${r.status}`);
              }
              window.toast(emailSent ? t("auth.toastResentSent") : t("auth.toastResent"));
            } catch (err) {
              window.toast(`${t("auth.toastResendFailed")}: ${err.message}`, { tone: "warn" });
            }
          }}
          onChangeEmail={() => setView(isSignUp ? "sign-up" : "sign-in")}
          onComplete={completeMagicLink}
        />
      ) : (
        <FormView
          isSignUp={isSignUp}
          email={email} setEmail={setEmail}
          name={name} setName={setName}
          company={company} setCompany={setCompany}
          valid={valid}
          submitting={submitting}
          onSubmit={sendMagicLink}
          onGoogle={continueWithGoogle}
          onPhone={continueWithPhone}
          onToggleMode={() => setView(isSignUp ? "sign-in" : "sign-up")}
        />
      )}
    </ModalShell>
  );
}

function FormView({
  isSignUp, email, setEmail, name, setName, company, setCompany,
  valid, submitting,
  onSubmit, onGoogle, onPhone, onToggleMode,
}) {
  const { t } = window.useT();
  const nameInvalid = isSignUp && name.length > 0 && name.trim().length < 2;
  const emailInvalid = email.length > 0 && !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email);
  return (
    /* P582 — `noValidate` disables the browser's built-in popup
       validation ("Please include an @ in the email address"). Our
       inline-error UI (`data-state="error"` on the input + an
       `auth-email-error` hint paragraph) is the canonical
       validation surface; the browser tooltip is redundant noise
       that breaks the design (positions outside the modal, doesn't
       respect dark-mode, can't be styled / localized). The
       server-side regex (request-link.js) is the actual gatekeeper —
       client-side validation is purely UX.
       P588 — `aria-busy` while submitting. The button text already
       changes from "Send magic link" → "Sending..." for sighted
       users; aria-busy gives SR users the same signal that the form
       is processing, so they don't try to re-submit during the
       in-flight request. */
    <form style={authStyles.col} onSubmit={onSubmit} autoComplete="on" noValidate aria-busy={submitting || undefined}>
      {/* Honeypot. Hidden visually + with autocomplete=off so password
          managers don't fill it. Keeps tab order out of it. Bots that
          walk every input by name will fill this and get silently
          dropped server-side. */}
      <div aria-hidden="true" style={authStyles.honeypotWrap}>
        <label>
          Company (do not fill)
          <input
            type="text"
            name="company"
            tabIndex={-1}
            autoComplete="off"
            value={company}
            onChange={(e) => setCompany(e.target.value)}
          />
        </label>
      </div>
      {isSignUp && (
        <Field label={t("auth.name")}>
          {/* P280 — aria-required so screen readers announce "Name,
              required, edit text" up front instead of only signaling
              required-ness via the post-submit aria-invalid. Same on
              the email input below. The native `required` attr was
              avoided because we have custom client-side validation +
              a custom Sending state; setting it would activate the
              browser's default validation popup which fights our own
              error UI. aria-required gives the SR cue without the
              browser interception. */}
          <input
            style={authStyles.field}
            placeholder={t("auth.namePlaceholder")}
            value={name}
            onChange={(e) => setName(e.target.value)}
            autoFocus
            disabled={submitting}
            maxLength={80}
            autoComplete="name"
            aria-required="true"
            data-state={nameInvalid ? "error" : undefined}
            aria-invalid={nameInvalid || undefined}
            aria-describedby="auth-name-error"
          />
          {/* P634 — Always-rendered error region with aria-live="polite"
              + aria-relevant="additions text" so the hint announces
              when the user types past the validity threshold. Pre-fix
              the `<div>` was conditionally rendered (`{nameInvalid &&
              <div>...</div>}`) — when the error first appeared, the
              new DOM element wasn't in any live region, so SR users
              heard silence even though the visual error appeared.
              Empty content when valid keeps layout neutral. Same
              shape as P633 fixed for FieldHint, applied per-input
              for the auth form (which doesn't share FieldHint). */}
          <div id="auth-name-error" data-hint-error aria-live="polite" aria-relevant="additions text">
            {nameInvalid ? t("auth.nameTooShort") : ""}
          </div>
        </Field>
      )}
      <Field label={t("auth.email")}>
        {/* P276 — pair the inline error hint with the input via
            aria-describedby so screen readers read "That doesn't
            look like a valid email" right after the input's name +
            value. Pre-fix the input was correctly aria-invalid but
            the hint was unowned text on the page; the SR user had
            to manually navigate to discover what was wrong. */}
        <input
          style={authStyles.field}
          type="email"
          placeholder={t("auth.emailPlaceholder")}
          value={email}
          onChange={(e) => setEmail(e.target.value.trim())}
          autoFocus={!isSignUp}
          disabled={submitting}
          maxLength={254}
          autoComplete="email"
          inputMode="email"
          // P591 — Mobile Enter key shows "Go" / "Send" instead of
          // generic Return — Enter on this email field commits the
          // form (sends magic link).
          enterKeyHint="send"
          autoCapitalize="none"
          autoCorrect="off"
          spellCheck={false}
          aria-required="true"
          data-state={emailInvalid ? "error" : undefined}
          aria-invalid={emailInvalid || undefined}
          aria-describedby="auth-email-error"
        />
        {/* P634 — see P634 note on the name-error region above. */}
        <div id="auth-email-error" data-hint-error aria-live="polite" aria-relevant="additions text">
          {emailInvalid ? t("auth.emailInvalid") : ""}
        </div>
      </Field>
      <button
        type="submit"
        disabled={!valid || submitting}
        style={{ ...authStyles.primaryBtn, ...((!valid || submitting) ? authStyles.btnDisabled : {}) }}
      >
        {submitting ? t("auth.magicLinkBtnSending") : t("auth.magicLinkBtn")}
      </button>

      <div style={authStyles.divider}>
        <span style={authStyles.dividerLine} />
        <span style={authStyles.dividerWord}>{t("auth.or")}</span>
        <span style={authStyles.dividerLine} />
      </div>

      <button type="button" style={authStyles.oauthBtn} onClick={onGoogle} disabled={submitting}>
        <GoogleGlyph /> {t("auth.continueGoogle")}
      </button>
      <button type="button" style={authStyles.oauthBtn} onClick={onPhone} disabled={submitting}>
        <Icon name="phone" size={16} style={{ color: "var(--accent)" }} /> {t("auth.continuePhone")}
      </button>

      <div style={authStyles.toggle}>
        <button type="button" style={authStyles.toggleLink} onClick={onToggleMode}>
          {isSignUp ? t("auth.toggleToSignIn") : t("auth.toggleToSignUp")}
        </button>
      </div>

      {isSignUp && (
        // P323 — was a hardcoded English string while the rest of
        // the modal honored useT(). A Russian or Uzbek visitor saw
        // the whole sign-up flow translated except this one
        // disclaimer they were being asked to agree to — both a UX
        // gap and a soft compliance issue (consent in a language
        // the user might not read). Now built from 5 i18n pieces
        // so each language wraps the link labels with its own
        // connectives + word order. Uzbek's verb phrase sits in
        // legalAfter (SOV) where in EN/RU it's just punctuation.
        <div style={authStyles.legal}>
          {t("auth.legalBefore")}
          <a
            href="/terms"
            style={authStyles.legalLink}
            onClick={window.spaNavOnClick("terms")}
          >{t("auth.legalTermsLabel")}</a>
          {t("auth.legalConjunction")}
          <a
            href="/privacy"
            style={authStyles.legalLink}
            onClick={window.spaNavOnClick("privacy")}
          >{t("auth.legalPrivacyLabel")}</a>
          {t("auth.legalAfter")}
        </div>
      )}
    </form>
  );
}

function SentView({ email, emailSent, onResend, onChangeEmail, onComplete }) {
  // P324 — was hardcoded English even though FormView already
  // honored useT(). Mirrors the P323 fix on the legal disclaimer:
  // body is split before/after so each language can place the email
  // at the right slot (Uzbek "Sehrli havolani <email> manziliga..."
  // wraps the email mid-clause, EN/RU prepend it).
  const { t } = window.useT();
  return (
    /* P581 — role="status" + aria-live="polite" so a SR user hears
       the "We sent a magic link to {email}" outcome when the modal
       transitions from FormView (filling email) to SentView (link
       sent). Pre-fix the form-submit happened, the modal re-rendered
       with this success state, and SR users heard nothing about
       what just happened. aria-atomic="true" so the visual + body +
       expiry hint announce as one chunk. Same shape as P580 fixed
       for the publish-modal success states. */
    <div style={authStyles.col} role="status" aria-live="polite" aria-atomic="true">
      <div style={authStyles.sentVisual}>
        <div style={authStyles.envelope}><Icon name="envelope" size={28} /></div>
        <p style={authStyles.sentBody}>
          {t("auth.sentBodyBefore")}<b style={{ color: "var(--ink)" }}>{email}</b>{t("auth.sentBodyAfter")}
        </p>
        {/* P560 — Expiry hint. Server-side TOKEN_TTL_SEC is 60*15
            (15 min). Users who don't get the email immediately (slow
            mail delivery, spam-filter delays) need to know whether
            to wait or resend; pre-fix they got no signal until they
            clicked an expired link and saw the "this link has
            expired" error page. The 15-minute window is the same
            value the server returns as `expiresIn`, so this matches
            server truth. Localized via new sentExpiry key. */}
        <p style={authStyles.sentExpiry}>{t("auth.sentExpiry")}</p>
      </div>
      <div style={authStyles.sentActions}>
        {/* P501 — explicit type="button" defensive against future
            <form> wrapping that would default these to type="submit". */}
        <button type="button" style={authStyles.secondaryBtn} onClick={onResend}>{t("auth.sentResend")}</button>
        <button type="button" style={authStyles.secondaryBtn} onClick={onChangeEmail}>{t("auth.sentChangeEmail")}</button>
      </div>
      {!emailSent && (
        <div style={authStyles.demoCard}>
          <div style={authStyles.demoKicker}>{t("auth.sentPreviewKicker")}</div>
          <p style={authStyles.demoBody}>
            {t("auth.sentPreviewBody")}
          </p>
          <button type="button" style={authStyles.primaryBtn} onClick={onComplete}>
            {t("auth.sentPreviewCta")} <span data-arrow aria-hidden="true">→</span>
          </button>
        </div>
      )}
    </div>
  );
}

function Field({ label, children }) {
  // <label> wrapping creates an implicit programmatic association with
  // the inner <input> — screen readers announce the label as part of
  // the input's accessible name without needing an id/htmlFor pair.
  // The visual label keeps the same fieldLabel styling.
  return (
    <label style={authStyles.fieldRow}>
      <span style={authStyles.fieldLabel}>{label}</span>
      {children}
    </label>
  );
}

function GoogleGlyph() {
  return (
    <svg width="16" height="16" viewBox="0 0 18 18" aria-hidden="true">
      <path fill="#4285F4" d="M17.64 9.2c0-.64-.06-1.25-.16-1.84H9v3.49h4.84a4.14 4.14 0 0 1-1.79 2.71v2.26h2.9c1.7-1.56 2.69-3.87 2.69-6.62z" />
      <path fill="#34A853" d="M9 18c2.43 0 4.46-.81 5.95-2.18l-2.9-2.26c-.8.54-1.83.86-3.05.86-2.34 0-4.32-1.58-5.03-3.71H.96v2.33A9 9 0 0 0 9 18z" />
      <path fill="#FBBC04" d="M3.97 10.71A5.41 5.41 0 0 1 3.68 9c0-.59.1-1.17.29-1.71V4.96H.96A9 9 0 0 0 0 9c0 1.45.35 2.83.96 4.04l3.01-2.33z" />
      <path fill="#EA4335" d="M9 3.58c1.32 0 2.51.45 3.44 1.35l2.58-2.58A8.95 8.95 0 0 0 9 0 9 9 0 0 0 .96 4.96L3.97 7.3C4.68 5.16 6.66 3.58 9 3.58z" />
    </svg>
  );
}

const authStyles = {
  col: { display: "flex", flexDirection: "column", gap: 14 },
  fieldRow: { display: "flex", flexDirection: "column", gap: 6 },
  fieldLabel: {
    fontFamily: "var(--font-mono)",
    fontSize: 11,
    letterSpacing: "0.08em",
    color: "var(--ink-3)",
    textTransform: "uppercase",
  },
  field: {
    width: "100%",
    padding: "10px 12px",
    border: "1px solid var(--rule)",
    borderRadius: 8,
    background: "var(--paper-2)",
    color: "var(--ink)",
    fontSize: 14,
    fontFamily: "var(--font-ui)",
    outline: "none",
  },
  primaryBtn: {
    width: "100%",
    padding: "11px 16px",
    background: "var(--ink)",
    color: "var(--paper)",
    borderRadius: 999,
    fontSize: 14,
    fontWeight: 500,
    fontFamily: "var(--font-ui)",
    transition: "opacity 0.15s",
  },
  secondaryBtn: {
    flex: 1,
    padding: "9px 14px",
    background: "transparent",
    border: "1px solid var(--rule)",
    color: "var(--ink-2)",
    borderRadius: 999,
    fontSize: 13,
    fontFamily: "var(--font-ui)",
  },
  oauthBtn: {
    width: "100%",
    padding: "10px 14px",
    background: "var(--paper-2)",
    border: "1px solid var(--rule)",
    color: "var(--ink)",
    borderRadius: 999,
    fontSize: 14,
    fontFamily: "var(--font-ui)",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 10,
  },
  phoneGlyph: { color: "var(--accent)", fontSize: 16 },
  btnDisabled: { opacity: 0.4, cursor: "not-allowed" },

  divider: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    margin: "4px 0",
  },
  dividerLine: { flex: 1, height: 1, background: "var(--rule)" },
  dividerWord: {
    fontFamily: "var(--font-mono)",
    fontSize: 10,
    letterSpacing: "0.12em",
    color: "var(--ink-3)",
  },

  toggle: {
    fontSize: 13,
    color: "var(--ink-2)",
    textAlign: "center",
    paddingTop: 6,
  },
  toggleLink: {
    color: "var(--accent)",
    background: "transparent",
    border: "none",
    padding: 0,
    fontSize: 13,
    fontFamily: "inherit",
    fontWeight: 500,
    cursor: "pointer",
    textDecoration: "underline",
    textUnderlineOffset: 2,
    textDecorationColor: "color-mix(in oklab, var(--accent) 40%, transparent)",
  },
  legal: {
    fontSize: 11,
    color: "var(--ink-3)",
    textAlign: "center",
    lineHeight: 1.5,
    paddingTop: 4,
  },
  legalLink: { color: "var(--ink-2)", textDecoration: "underline" },
  honeypotWrap: {
    // Off-screen but still focusable-by-bot. Use absolute + small size
    // rather than display:none so headless browsers driven by Selenium
    // & friends don't trivially detect it.
    position: "absolute",
    left: "-9999px",
    width: 1,
    height: 1,
    overflow: "hidden",
    opacity: 0,
    pointerEvents: "none",
  },

  sentVisual: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: 16,
    padding: "16px 0 8px",
    textAlign: "center",
  },
  envelope: {
    width: 64,
    height: 64,
    borderRadius: 999,
    background: "color-mix(in oklab, var(--accent) 15%, var(--paper-2))",
    color: "var(--accent)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 28,
    border: "1px solid color-mix(in oklab, var(--accent) 30%, var(--rule))",
    boxShadow: "0 0 0 0 color-mix(in oklab, var(--accent) 35%, transparent)",
    animation: "envelope-pulse 2.4s ease-in-out infinite",
  },
  sentBody: {
    fontSize: 14,
    color: "var(--ink-2)",
    lineHeight: 1.5,
    maxWidth: 360,
    textWrap: "pretty",
  },
  // P560 — quieter expiry hint underneath sentBody. Same width
  // boundary, smaller mono-flavored font + ink-3 muted color so it
  // reads as secondary info, not part of the main message.
  sentExpiry: {
    fontSize: 12,
    color: "var(--ink-3)",
    lineHeight: 1.5,
    maxWidth: 360,
    margin: "8px 0 0",
    fontFamily: "var(--font-mono)",
    letterSpacing: "0.02em",
  },
  sentActions: { display: "flex", gap: 8 },
  demoCard: {
    marginTop: 6,
    padding: 14,
    background: "color-mix(in oklab, var(--accent) 7%, var(--paper-2))",
    border: "1px dashed color-mix(in oklab, var(--accent) 35%, var(--rule))",
    borderRadius: 10,
    display: "flex",
    flexDirection: "column",
    gap: 10,
  },
  demoKicker: {
    fontFamily: "var(--font-mono)",
    fontSize: 10,
    letterSpacing: "0.1em",
    color: "var(--accent)",
  },
  demoBody: { fontSize: 12, color: "var(--ink-2)", lineHeight: 1.5 },
};

window.AuthModal = AuthModal;
